/**
 * Custom Product Recommendations Engine
 * Brand-based recommendations with type fallbacks
 */

class CustomRecommendations {
  constructor() {
    this.init();
  }

  init() {
    // Find all custom recommendation blocks
    const blocks = document.querySelectorAll('[data-custom-recommendations-block]');
    blocks.forEach(block => {
      this.loadRecommendations(block);
    });
  }

  async loadRecommendations(block) {
    const container = block.querySelector('[id^="custom-recommendations-container-"]');
    if (!container) return;

    const sectionId = container.dataset.sectionId;
    const limit = parseInt(container.dataset.limit);
    const productId = container.dataset.productId;
    const productTitle = container.dataset.productTitle;
    const collections = container.dataset.collections ? container.dataset.collections.split(',') : [];

    try {
      // Extract brand from product title (assume first word)
      const brand = this.extractBrand(productTitle);

      // Get type collections (exclude brand collections)
      const typeCollections = this.getTypeCollections(collections, brand);

      // Fetch recommendations
      const recommendations = await this.fetchRecommendations(brand, typeCollections, limit, productId);

      // Render recommendations
      this.renderRecommendations(container, recommendations, limit);

    } catch (error) {
      console.error('Error loading custom recommendations:', error);
      this.showError(container);
    }
  }

  extractBrand(productTitle) {
    // Assume brand is the first word before space
    const words = productTitle.trim().split(' ');
    return words[0].toLowerCase();
  }

  getTypeCollections(collections, brand) {
    // Assume brand collections contain the brand name, type collections don't
    return collections.filter(handle => !handle.toLowerCase().includes(brand));
  }

  async fetchRecommendations(brand, typeCollections, limit, excludeProductId) {
    const recommendations = [];
    const seenIds = new Set([excludeProductId]);

    // First, try brand collection
    if (brand) {
      const brandProducts = await this.fetchCollectionProducts(brand);
      for (const product of brandProducts) {
        if (!seenIds.has(product.id.toString()) && recommendations.length < limit) {
          recommendations.push(product);
          seenIds.add(product.id.toString());
        }
      }
    }

    // Then, type collections
    for (const typeHandle of typeCollections) {
      if (recommendations.length >= limit) break;
      const typeProducts = await this.fetchCollectionProducts(typeHandle);
      for (const product of typeProducts) {
        if (!seenIds.has(product.id.toString()) && recommendations.length < limit) {
          recommendations.push(product);
          seenIds.add(product.id.toString());
        }
      }
    }

    // Finally, fill with best-selling products if needed
    if (recommendations.length < limit) {
      const bestSelling = await this.fetchBestSellingProducts(limit - recommendations.length, seenIds);
      recommendations.push(...bestSelling);
    }

    return recommendations.slice(0, limit);
  }

  async fetchCollectionProducts(collectionHandle) {
    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=50`);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.warn(`Failed to fetch collection ${collectionHandle}:`, error);
      return [];
    }
  }

  async fetchBestSellingProducts(count, excludeIds) {
    try {
      // Use search API with sort by best-selling
      const query = Array.from(excludeIds).map(id => `-id:${id}`).join(' ');
      const response = await fetch(`/search.json?q=${encodeURIComponent(query)}&sort_by=best-selling&limit=${count}`);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.warn('Failed to fetch best-selling products:', error);
      return [];
    }
  }

  renderRecommendations(container, products, limit) {
    const productsContainer = container.querySelector('.products-grid') || container.querySelector('.products-carousel');

    if (!productsContainer) return;

    // Clear loading
    const loading = container.querySelector('.custom-recommendations-loading');
    if (loading) loading.remove();

    // Render products
    products.forEach((product, index) => {
      const productHtml = this.buildProductCard(product, index);
      productsContainer.insertAdjacentHTML('beforeend', productHtml);
    });

    // Initialize slider if needed
    if (productsContainer.classList.contains('products-carousel')) {
      // Assuming the theme has a slider initialization function
      if (window.SMI_Slider2) {
        new SMI_Slider2(productsContainer).init();
      }
    }
  }

  buildProductCard(product, index) {
    const image = product.images && product.images[0] ? product.images[0] : null;
    const imageUrl = image ? image.src.replace(/(\.[^.]*)$/, '_300x300$1') : '';
    const price = product.variants && product.variants[0] ? product.variants[0].price : 0;
    const comparePrice = product.variants && product.variants[0] ? product.variants[0].compare_at_price : null;

    return `
      <div class="product">
        <div class="product-card">
          <div class="product-card__image">
            <a href="/products/${product.handle}">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : ''}
            </a>
          </div>
          <div class="product-card__info">
            <h3 class="product-card__title">
              <a href="/products/${product.handle}">${product.title}</a>
            </h3>
            <div class="product-card__price">
              ${comparePrice ? `<span class="price--compare">${this.formatMoney(comparePrice)}</span>` : ''}
              <span class="price">${this.formatMoney(price)}</span>
            </div>
            <div class="product-card__actions">
              <button class="btn btn--primary" onclick="window.location.href='/products/${product.handle}'">
                View Product
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  formatMoney(amount) {
    // Simple money formatting - adjust based on shop currency
    return '$' + (amount / 100).toFixed(2);
  }

  showError(container) {
    const loading = container.querySelector('.custom-recommendations-loading');
    if (loading) {
      loading.innerHTML = 'Unable to load recommendations at this time.';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CustomRecommendations();
});

// Also initialize on theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new CustomRecommendations();
  });
}
